import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { PressScan } from "@/pages/nostalgie/data/pressScans";

interface PressScanFeatureProps {
  index: number;
  scans: PressScan[];
}

export default function PressScanFeature({ index, scans }: PressScanFeatureProps) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);

  const step = useCallback(
    (direction: number) => {
      setOpenIndex((prev) => {
        if (prev === null) return prev;
        const next = (prev + direction + scans.length) % scans.length;
        return next;
      });
    },
    [scans.length],
  );

  useEffect(() => {
    if (openIndex === null) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [openIndex, close, step]);

  return (
    <article className="md:col-span-2 xl:col-span-3 bg-background-100 border border-background-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-4 p-6 md:p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
              <i className="ri-newspaper-line text-lg"></i>
            </span>
            <div className="min-w-0">
              <div className="text-xs font-semibold tracking-[0.2em] text-accent-700">
                {t(`nost_press_${index}_year`)}
              </div>
              <div className="text-sm text-foreground-600 leading-snug">
                {t(`nost_press_${index}_source`)}
              </div>
            </div>
          </div>

          <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-3">
            {t(`nost_press_${index}_title`)}
          </h3>

          <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-5">
            {t(`nost_press_${index}_text`)}
          </p>

          <div className="mt-auto inline-flex items-start gap-2 text-xs text-foreground-500 leading-relaxed">
            <i className="ri-zoom-in-line text-base"></i>
            <span>{t(`nost_press_${index}_scan_note`)}</span>
          </div>
        </div>

        <div className="lg:col-span-8 p-4 md:p-6 bg-background-50 border-t lg:border-t-0 lg:border-l border-background-200">
          <div
            className={
              scans.length > 1
                ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
                : "flex justify-center"
            }
          >
            {scans.map((scan, position) => (
              <button
                key={scan.captionKey}
                type="button"
                onClick={() => setOpenIndex(position)}
                className={`group relative block w-full text-left rounded-lg overflow-hidden border border-background-200 bg-background-100 cursor-pointer ${
                  scans.length > 1 ? "" : "max-w-[240px] sm:max-w-[260px]"
                }`}
              >
                <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden">
                  <img
                    src={scan.src}
                    alt={t(scan.captionKey)}
                    title={t(`nost_press_${index}_source`)}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/25 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity w-11 h-11 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-900">
                    <i className="ri-zoom-in-line text-xl"></i>
                  </span>
                </div>
                <div className="px-3 py-2 border-t border-background-200 bg-background-100">
                  <p className="text-xs text-foreground-600 truncate">
                    {t(scan.captionKey)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {openIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label={t("nost_press_2_close")}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 shadow-lg cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {scans.length > 1 && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  step(-1);
                }}
                aria-label="Vorherige Seite"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  step(1);
                }}
                aria-label="Nächste Seite"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </>
          )}

          <div
            className="max-w-4xl max-h-[85vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={scans[openIndex].src}
              alt={t(scans[openIndex].captionKey)}
              className="max-w-full max-h-[80vh] object-contain rounded-lg bg-white"
            />
            <p className="text-white text-sm text-center mt-3">
              {t(scans[openIndex].captionKey)}
              {scans.length > 1 && (
                <span className="text-white/60 ml-2">
                  ({openIndex + 1} / {scans.length})
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </article>
  );
}